// ─────────────────────────────────────────────
//  useRoomCases — the day's OR cases for a facility, indexed by room.
//
//  Read side of Room Status. The write side is useCasePhases; nothing else
//  subscribes to or_cases.
//
//  ⚠️ `date` IS THE FACILITY'S DAY, AND THE CALLER OWNS IT. Pass
//  `dayKey(new Date(), facilityTz(facility))` (or useDayKey(tz)), never
//  `rosterDateKey`. Two day-key definitions still coexist in this codebase and
//  the device-local one files documents under a key nobody queries on a
//  mis-set device — no error, no empty state, just a board that forgot a case.
//
//  ⚠️ "NOT LOADED" IS NOT "NO CASES". Until the first snapshot arrives, and on
//  any listener error, this reports `loading` / `error` rather than an empty
//  list. A screen that renders "no case in this room" because a read failed is
//  the confidently-wrong board the whole feature is trying to avoid.
// ─────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { listenToCases } from "../firebase/firestore";

// Minutes since a Firestore Timestamp / Date / millis value. Null when there is
// nothing to measure — a caller must render "—", never "0 minutes ago", which
// would assert a stamp that does not exist.
export function minutesSince(at, now = Date.now()) {
  if (!at) return null;
  const ms =
    typeof at?.toMillis === "function"
      ? at.toMillis()
      : at instanceof Date
        ? at.getTime()
        : typeof at === "number"
          ? at
          : null;
  if (ms == null || Number.isNaN(ms)) return null;
  const mins = Math.floor((now - ms) / 60000);
  // A clock skewed a little into the future reads as "just now", not negative.
  return mins < 0 ? 0 : mins;
}

// The room's live case. A room should only ever have one open case, but if a
// bad write ever leaves two, the HIGHEST caseNumber wins — that is the one the
// team is actually in, and silently picking the older one would let taps land
// on a finished case.
export function openCaseOf(cases, roomId) {
  if (!roomId) return null;
  const open = (Array.isArray(cases) ? cases : []).filter(
    (c) => c && c.roomId === roomId && c.state !== "closed"
  );
  if (!open.length) return null;
  return open.reduce((best, c) =>
    (c.caseNumber || 0) > (best.caseNumber || 0) ? c : best
  );
}

export default function useRoomCases(facilityId, date) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // No facility or no day is not an empty day — it is a caller that cannot
    // ask the question yet. Stay in `loading` rather than claiming zero cases.
    if (!facilityId || !date) {
      setCases([]);
      setLoading(true);
      setError(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    const unsub = listenToCases(
      facilityId,
      date,
      (snap) => {
        if (!alive) return;
        setCases(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        console.error("[RoomStatus] or_cases listener failed:", err);
        setError(err);
        setLoading(false);
      }
    );
    return () => {
      alive = false;
      if (typeof unsub === "function") unsub();
    };
  }, [facilityId, date]);

  const openByRoomId = useMemo(() => {
    const map = {};
    for (const c of cases) {
      if (!c?.roomId || c.state === "closed") continue;
      const prev = map[c.roomId];
      if (!prev || (c.caseNumber || 0) > (prev.caseNumber || 0)) map[c.roomId] = c;
    }
    return map;
  }, [cases]);

  return {
    cases,
    openByRoomId,
    openCaseFor: (roomId) => openByRoomId[roomId] || null,
    loading,
    error,
  };
}
